import * as React from "react";
import { Icon } from "../actions/Icon";

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

/**
 * Dropdown for settings choices and roles. A native <select> styled to the DS field, so keyboard,
 * screen-reader and mobile pickers all behave as the platform does.
 */
export interface SelectProps extends Omit<React.SelectHTMLAttributes<HTMLSelectElement>, "onChange" | "value" | "defaultValue" | "size"> {
  options: SelectOption[];
  /** Controlled value. Omit for uncontrolled (use defaultValue). */
  value?: string;
  defaultValue?: string;
  onChange?: (value: string) => void;
  /** Visible label above the field, associated with it. */
  label?: React.ReactNode;
  /** Shown (and not selectable again) while no option is chosen. */
  placeholder?: string;
  /** "sm" fits inside another field, e.g. the role picker in ChipInput. Default "md". */
  size?: "sm" | "md";
}

export function Select({
  options,
  value,
  defaultValue,
  onChange,
  label,
  placeholder,
  size = "md",
  className,
  id,
  ...rest
}: SelectProps): JSX.Element {
  const generatedId = React.useId();
  const selectId = id ?? generatedId;
  // With a placeholder and nothing chosen, select the hidden placeholder option.
  const initial = value === undefined && defaultValue === undefined && placeholder !== undefined ? "" : defaultValue;
  const cls = ["gk-select", size === "sm" && "gk-select--sm", className].filter(Boolean).join(" ");
  return (
    <div className={cls}>
      {label !== undefined && (
        <label className="gk-select__label" htmlFor={selectId}>
          {label}
        </label>
      )}
      <span className="gk-select__box">
        <select
          id={selectId}
          className="gk-select__control"
          value={value === undefined ? undefined : value}
          defaultValue={value === undefined ? initial : undefined}
          onChange={(e) => onChange?.(e.target.value)}
          {...rest}
        >
          {placeholder !== undefined && (
            <option value="" disabled hidden>
              {placeholder}
            </option>
          )}
          {options.map((o) => (
            <option key={o.value} value={o.value} disabled={o.disabled}>
              {o.label}
            </option>
          ))}
        </select>
        <Icon name="caret-down" size={16} className="gk-select__caret" />
      </span>
    </div>
  );
}
